import { Avatar, Box, IconButton, Tooltip } from '@mui/material'
import { observer } from 'mobx-react-lite'
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { useNavigate } from 'react-router-dom'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { GoBackButtonWithArrow } from 'src/component/common/original/molecule/GoBackButtonWithArrow'
import { UserSkillGapAnalysis } from '../component/UserSkillGapAnalysis'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useUserStore } from 'src/feature/user/store/user.store'
import { UserUnusedSkillsList } from 'src/feature/unused-skills/component/organism/UserUnusedSkillsList'
import { UserLearningMetrics } from '../component/UserLearningMetrics'
import { Word } from 'src/feature/localization/component/atom/Word'
import { CareerCard } from 'src/feature/profile/component/organism/CareerCard'
import { UserPerformanceReviews } from '../component/UserPerformanceReviews'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { Palette } from 'src/feature/theme/model/Palette'
import { API_BASE_URL } from 'src/util/constant'

export type InitialUserMeta = {
  userId: string
  name: string
  email: string
  avatarUrl: string
  memberSince: string
}

export type AdministratorUserDetailsPageProps = {
  isSidePanel?: boolean
  onClosePanel?: () => void
  initialUserMeta: InitialUserMeta
}

const absoluteUrlPattern = /^(?:https?:)?\/\//i

const resolveAvatarSrc = (avatarUrl: string) => {
  if (!avatarUrl) {
    return undefined
  }

  if (absoluteUrlPattern.test(avatarUrl) || avatarUrl.startsWith('data:') || avatarUrl.startsWith('blob:')) {
    return avatarUrl
  }

  const normalizedBase = API_BASE_URL.replace(/\/+$/, '')
  const normalizedPath = avatarUrl.replace(/^\/+/, '')

  return `${normalizedBase}/${normalizedPath}`
}

const getUserInitials = (name?: string) => {
  if (!name) {
    return ''
  }

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('')

  return initials || name.slice(0, 2).toUpperCase()
}

const formatMemberSince = (value?: string) => {
  if (!value) {
    return undefined
  }

  const parsedDate = new Date(value)

  return Number.isNaN(parsedDate.getTime()) ? value : parsedDate.toLocaleDateString()
}

export const AdministratorUserDetailsPage = observer(({ isSidePanel = false, onClosePanel, initialUserMeta }: AdministratorUserDetailsPageProps) => {
  const { translation } = useLocalizationStore()
  const userStore = useUserStore()
  const navigate = useNavigate()
  const { userId, name, email, avatarUrl, memberSince } = initialUserMeta
  const userDetailsTranslation = translation.pages.myTeam.userDetailsDrawer
  const userLabel = translation.pages.signUp.user()

  const isSkillGap = (userStore.sitemap?.modules ?? []).some(s => s.key === 'skillGap')
  const unavailableLabel = userDetailsTranslation.unavailable()
  const userName = name || unavailableLabel
  const hasUserName = userName !== unavailableLabel
  const userEmail = email
  const avatarSrc = resolveAvatarSrc(avatarUrl)
  const formattedMemberSince = formatMemberSince(memberSince)
  const profileCardMemberSince = translation.pages.profileInfo.profileCard.memberSince()
  const shouldShowProfileSummary = hasUserName || Boolean(userEmail) || Boolean(formattedMemberSince) || Boolean(avatarSrc)

  const handleOpenFullPage = () => {
    onClosePanel?.()
    navigate(appRoutePath.MyTeamUserDetails(userId), {
      state: {
        initialUserMeta,
      },
    })
  }

  return (
    <Box sx={isSidePanel ? styles.panelRoot : styles.pageRoot}>
      <AppBox alignItems='center' gap={5} justifyContent='space-between'>
        {!isSidePanel && (
          <AppBox alignItems='baseline' gap={5}>
            <GoBackButtonWithArrow textValue={userLabel} />
            {hasUserName && <Word sx={styles.userNameSuffix}>{` . ${userName}`}</Word>}
          </AppBox>
        )}

        {isSidePanel && (
          <AppBox alignItems='center' justifyContent='flex-end' width='100%' gap={5}>
            <Tooltip title={userDetailsTranslation.openFullPage()} arrow>
              <IconButton onClick={handleOpenFullPage} sx={styles.panelActionButton}>
                <OpenInFullRoundedIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title={userDetailsTranslation.closePanel()} arrow>
              <IconButton onClick={onClosePanel} sx={styles.panelActionButton}>
                <CloseRoundedIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </AppBox>
        )}
      </AppBox>
      {shouldShowProfileSummary && (
        <AppBox sx={styles.profileSummary}>
          <Avatar src={avatarSrc} sx={styles.profileAvatar}>{getUserInitials(userName)}</Avatar>
          <AppBox sx={styles.profileTextWrap}>
            {hasUserName && <Word sx={styles.profileName}>{userName}</Word>}
            {userEmail && <Word sx={styles.profileMeta}>{userEmail}</Word>}
            {formattedMemberSince && <Word sx={styles.profileMeta}>{`${profileCardMemberSince}: ${formattedMemberSince}`}</Word>}
          </AppBox>
        </AppBox>
      )}
      <CareerCard userId={userId} />
      <UserPerformanceReviews userId={userId} />
      <UserLearningMetrics userId={userId} isSidePanel={isSidePanel} />
      {isSkillGap && (
        <AppBox
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
          }}
          gap={15}
          width='100%'
          maxHeight={{ xs: '100%', md: '50vh' }}
        >
          <UserSkillGapAnalysis userId={userId} />
          <UserUnusedSkillsList userId={userId} />
        </AppBox>
      )}
    </Box>
  )
})

const styles = {
  pageRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
    padding: { xs: '1rem', md: '1rem 5rem' },
  },
  panelRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '100%',
    paddingTop: '1rem',
    paddingRight: '1rem',
    paddingLeft: '1rem',
    paddingBottom: '2rem',
    backgroundColor: 'background.dark',
    '& > *': {
      width: '100%',
      boxSizing: 'border-box',
    },
    '& > :last-child': {
      marginBottom: '1rem',
      paddingBottom: '0.5rem',
    },
  },
  panelActionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1.5px solid transparent',
    borderRadius: '50%',
    transition: 'border-color 0.3s ease, background-color 0.3s ease',
    '&:hover': {
      borderColor: Palette.SMOKY_SLATE,
      backgroundColor: Palette.WHITE,
    },
    color: Palette.SMOKY_SLATE,
    width: '1.2rem',
    height: '1.2rem',
  },
  profileSummary: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  profileAvatar: {
    width: 100,
    height: 100,
    flexShrink: 0,
  },
  profileTextWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 0,
    gap: 5,
  },
  profileName: {
    color: 'text.primary',
    fontWeight: 600,
    fontSize: 20,
    lineHeight: 1.2,
  },
  profileMeta: {
    color: 'text.secondary',
    fontSize: 12,
    lineHeight: 1.2,
  },
  userNameSuffix: {
    color: 'text.secondary',
    fontSize: 12,
  },
}
